import { Dispatch, FC, SetStateAction } from 'react';

import { RequestStatus, SearchAktivitetResultSummary, SearchBebyggelseobjektResultSummary } from 'models';

import classNames from 'classnames';

interface PaginationProps {
  results: SearchAktivitetResultSummary | SearchBebyggelseobjektResultSummary | undefined;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  requestStatus: RequestStatus;
}

const pageSpan = (result?: SearchBebyggelseobjektResultSummary | SearchAktivitetResultSummary): string => {
  if (!result) {
    return '0';
  }

  const first = result.total_size > 0 ? result.page * result.page_size + 1 : 0;
  const max = (result.page + 1) * result.page_size;
  const last = result.total_size < max ? result.total_size : max;
  return `${first} - ${last}`;
};

const hasMorePages = (summary: SearchAktivitetResultSummary | SearchBebyggelseobjektResultSummary | undefined) => {
  return summary?.total_size && summary.total_size > (summary.page + 1) * summary.page_size;
};

const PagingPosition: FC<{
  searchResult?: SearchAktivitetResultSummary | SearchBebyggelseobjektResultSummary;
}> = ({ searchResult }) => {
  if (!searchResult) {
    return null;
  }
  const width =
    searchResult && searchResult.total_size > 0 ? (searchResult.results.length * 100) / searchResult.total_size : 100;
  const offset = (searchResult.page_size * 100) / searchResult.total_size;

  return (
    <>
      {searchResult && (
        <div
          className="tw-relative tw-flex tw-justify-center tw-gap-1 tw-bg-raa-gray-3 md:tw-justify-between"
          style={{ height: '8px' }}
        >
          <span
            className="tw-absolute tw-bg-raa-gray-4"
            style={{
              left: `${searchResult.page * offset}%`,
              width: `${width}%`,
              height: '100%',
              transition: 'left 250ms ease-out, width 250ms ease-out'
            }}
          >
            &nbsp;
          </span>
        </div>
      )}
    </>
  );
};

const Pagination: FC<PaginationProps> = ({ results, setCurrentPage, requestStatus }) => {
  return (
    <div className={classNames('tw-bg-raa-gray-2')}>
      <PagingPosition searchResult={results} />

      <div className="tw-flex tw-justify-center tw-gap-1 tw-px-3 tw-py-3 md:tw-justify-between md:tw-p-3">
        <button
          className={`tw-group tw-flex tw-items-center tw-gap-1 tw-rounded tw-bg-raa-gray-1 tw-px-1 tw-text-raa-orange focus:tw-bg-raa-orange focus:tw-text-raa-white focus:tw-outline-transparent ${
            results?.page === 0 ? 'tw-text-raa-gray-3' : ''
          }`}
          disabled={results?.page === 0}
          title="Föregående"
          onClick={() => setCurrentPage((results?.page ?? 1) - 1)}
          aria-label="Visa föregående sida av sökresultat"
        >
          <span className="raa-icon raa-icon-arrow-left tw-text-size-2.5" />
        </button>
        <div className={classNames('tw-flex tw-h-10 tw-items-center', { 'tw-invisible': requestStatus === 'pending' })}>
          <span>
            Visar {pageSpan(results)} av {results == null ? 0 : results?.total_size}
          </span>
        </div>
        <button
          className={`tw-group tw-flex tw-items-center tw-gap-1 tw-rounded tw-bg-raa-gray-1 tw-px-1 tw-text-raa-orange focus:tw-bg-raa-orange focus:tw-text-raa-white focus:tw-outline-transparent ${
            !hasMorePages(results) ? 'tw-text-raa-gray-3' : ''
          }`}
          disabled={!hasMorePages(results)}
          title="Nästa"
          onClick={() => setCurrentPage((results?.page ?? 0) + 1)}
          aria-label="Visa nästa sida av sökresultat"
        >
          <span className="raa-icon raa-icon-arrow-right tw-text-size-2.5" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
